import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { Button } from '@rocco/components/button';
import { ExternalLink, MessageCircleIcon } from 'lucide-react';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import type { DropdownItem } from '~/modules/enquiry/component/enquiry-form/EnquiryForm';
import type { Branch } from '~/modules/enquiry/util/extract-branch-from-network.server';

interface PageDetailHeaderCTAProps {
    /** The product page ID. */
    pageID: number;
    /** The professional's website URL. */
    website?: string | null;
    /** The professional's ID. */
    professionalID: number;
    /** The professional's title/name. */
    professionalTitle: string;
    /** The CTA label from the professional's settings (e.g., "Request Price / Brochure"). */
    primaryCTALabel: string;
    /** Branch options for the enquiry modal dropdown. */
    branchOptions: DropdownItem<Branch>[];
    /** The professional's company logo URL. */
    companyLogo: string;
    /** The professional's logo background colour. */
    logoBackgroundColor: string;
}

/**
 * The CTA buttons in the product detail page header.
 * Displays a "Visit Website" button (if available) and an enquiry button.
 */
const PageDetailHeaderCTA = ({
    pageID,
    website,
    professionalID,
    professionalTitle,
    primaryCTALabel,
    branchOptions,
    companyLogo,
    logoBackgroundColor,
}: PageDetailHeaderCTAProps) => {
    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(website, {
            libraryItemID: pageID,
            professionalID: professionalID,
            professionalTitle,
        });

    const { openEnquiryModal } = useEnquiryModal({
        professional: {
            ID: professionalID,
            Title: professionalTitle,
            branchOptions,
            CompanyLogo: companyLogo,
            LogoBackgroundColor: logoBackgroundColor,
        },
        itemType: 'product',
        siteTreeID: pageID,
        ctaLabel: primaryCTALabel,
        includeBranch: branchOptions.length > 0,
    });

    return (
        <div className="flex gap-4">
            {referralTrackingURL && (
                <Button
                    color="secondary"
                    weight="default"
                    size="larger"
                    asChild
                >
                    <a
                        href={referralTrackingURL}
                        target="_blank"
                        {...referralTrackingParams}
                        rel="noopener"
                    >
                        Visit Website
                        <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                </Button>
            )}
            <Button
                color="dark"
                weight="default"
                size="larger"
                onClick={() => openEnquiryModal()}
            >
                {primaryCTALabel}
                <MessageCircleIcon className="ml-2 h-4 w-4" />
            </Button>
        </div>
    );
};

export default PageDetailHeaderCTA;
